import React from "react";
import {
    Typography,
    Box,
    Avatar,
    Badge,
    Divider,
    Grow,
    Paper,
    ClickAwayListener,
    Button as MuiButton,
    Popper,
} from "@mui/material";
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    MenuOutlined,
    UserOutlined,
    HomeOutlined,
    DashboardOutlined,
    TeamOutlined,
    CalendarOutlined,
    FileTextOutlined,
    BellOutlined,
    SettingOutlined,
    ProfileOutlined,
} from "@ant-design/icons";
import { Button as AntButton, Breadcrumb } from "antd";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useLocation, matchPath, useNavigate, Link } from "react-router-dom";
import NotificationBell from "../components/NotificationBell";
import { useIsMobile } from "../../hooks/useIsMobile";

interface CrumbItem {
    title: string;
    to?: string;
}

interface HeaderBarProps {
    collapsed: boolean;
    setCollapsed: (value: boolean) => void;
    mobileOpen: boolean;
    setMobileOpen: (value: boolean) => void;
    fullName: string;
    azureImageUrl: string;
    imageError: boolean;
    setImageError: (value: boolean) => void;
    handleLogout: () => void;
    SIDEBAR_WIDTH: number;
}

const HeaderBar: React.FC<HeaderBarProps> = ({
    collapsed,
    setCollapsed,
    mobileOpen,
    setMobileOpen,
    fullName,
    azureImageUrl,
    imageError,
    setImageError,
    handleLogout,
    SIDEBAR_WIDTH,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleMenuToggle = () => {
        if (isMobile) {
            setMobileOpen(!mobileOpen);
        } else {
            setCollapsed(!collapsed);
        }
    };

    const getPageData = () => {
        let data = {
            title: "NeoView",
            icon: <SettingOutlined style={{ color: "#1677fa" }} />,
            subtitle: "Stay organized and productive",
            breadcrumb: [{ title: "Home", to: "/home" }] as CrumbItem[],
        };
        if (matchPath("/home", location.pathname)) {
            data = {
                title: "Home",
                icon: <HomeOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Welcome to NeoView — your unified control center",
                breadcrumb: [{ title: "Home" }],
            };
        } else if (matchPath("/dashboard", location.pathname)) {
            data = {
                title: "Dashboard",
                icon: <DashboardOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Overview of patient and appointment analytics",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Dashboard" }],
            };
        } else if (matchPath("/patients", location.pathname)) {
            data = {
                title: "Patients",
                icon: <TeamOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Manage patient records and reports",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Patients" }],
            };
        } else if (matchPath("/examination/:patientId", location.pathname)) {
            data = {
                title: "Examinations",
                icon: <FileTextOutlined style={{ color: "#1677fa" }} />,
                subtitle: "View and edit medical examination details",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Patients", to: "/patients" }, { title: "Examination" }],
            };
        } else if (matchPath("/appointments", location.pathname)) {
            data = {
                title: "Appointments",
                icon: <CalendarOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Track and schedule upcoming appointments",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Appointments" }],
            };
        } else if (matchPath("/management/users", location.pathname)) {
            data = {
                title: "Users",
                icon: <UserOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Manage application users and permissions",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Management" }, { title: "Users" }],
            };
        } else if (matchPath("/management/notifications", location.pathname)) {
            data = {
                title: "Notifications",
                icon: <BellOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Create and manage system notifications",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Management" }, { title: "Notifications" }],
            };
        } else if (matchPath("/management/changelogs", location.pathname)) {
            data = {
                title: "Change Logs",
                icon: <FileTextOutlined style={{ color: "#1677fa" }} />,
                subtitle: "View and edit system changelogs",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Management" }, { title: "Changelog" }],
            };
        } else if (matchPath("/changelog", location.pathname)) {
            data = {
                title: "What's New",
                icon: <FileTextOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Browse and review What's New entries",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "What's New" }],
            };
        } else if (matchPath("/profile", location.pathname)) {
            data = {
                title: "Profile",
                icon: <ProfileOutlined style={{ color: "#1677fa" }} />,
                subtitle: "Your personal and clinic information",
                breadcrumb: [{ title: "Home", to: "/home" }, { title: "Profile" }],
            };
        }
        return data;
    };

    const { title, icon, subtitle, breadcrumb } = getPageData();

    const headerLeft = isMobile ? 12 : (collapsed ? 80 : SIDEBAR_WIDTH) + 32;
    const menuIcon = isMobile
        ? <MenuOutlined />
        : collapsed
            ? <MenuUnfoldOutlined />
            : <MenuFoldOutlined />;

    return (
        <header
            style={{
                position: "fixed",
                top: isMobile ? 8 : 16,
                left: headerLeft,
                right: isMobile ? 8 : 16,
                height: isMobile ? 56 : 72,
                backgroundColor: "#ffffff",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #f0f0f0",
                borderRadius: isMobile ? 10 : 12,
                padding: isMobile ? "0 12px" : "0 24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                transition: "all 0.3s ease",
                gap: 8,
            }}
        >
            {/* Left: Menu + Breadcrumb */}
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ minWidth: 0, flex: isMobile ? "0 0 auto" : "1 1 0" }}
            >
                <AntButton
                    type="text"
                    aria-label={isMobile ? "Open navigation" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    icon={menuIcon}
                    onClick={handleMenuToggle}
                    style={{ fontSize: 18, color: "#444", flexShrink: 0 }}
                />
                {!isMobile && (
                    <Breadcrumb
                        separator="›"
                        items={breadcrumb.map((item) => ({
                            title: item.to ? <Link to={item.to}>{item.title}</Link> : <span>{item.title}</span>,
                        }))}
                        style={{ marginLeft: 8, fontSize: 13, color: "#6b7280" }}
                    />
                )}
            </Box>

            {/* Center Title */}
            <Box
                sx={{
                    position: isMobile ? "relative" : "absolute",
                    left: isMobile ? "auto" : "50%",
                    transform: isMobile ? "none" : "translateX(-50%)",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    userSelect: "none",
                    minWidth: 0,
                    flex: isMobile ? "1 1 auto" : "0 0 auto",
                    px: isMobile ? 1 : 0,
                }}
            >
                <Grow in timeout={400}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            maxWidth: "100%",
                        }}
                    >
                        {!isMobile && icon}
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                fontWeight: 700,
                                fontSize: isMobile ? "1.05rem" : "1.35rem",
                                color: "#1f2937",
                                background: "linear-gradient(90deg, #1677fa 0%, #52c41a 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                </Grow>
                {!isMobile && (
                    <Typography
                        variant="caption"
                        sx={{ color: "#6b7280", fontSize: "0.85rem", marginTop: "2px" }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>

            {/* Right Section */}
            <Box
                display="flex"
                alignItems="center"
                gap={isMobile ? 1 : 2}
                sx={{ flex: isMobile ? "0 0 auto" : "1 1 0", justifyContent: "flex-end" }}
            >
                {!isMobile && (
                    <Box textAlign="right">
                        <Typography variant="body2" sx={{ fontSize: 12, opacity: 0.7 }}>
                            Welcome,
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="medium" sx={{ fontSize: 14 }}>
                            {fullName}
                        </Typography>
                    </Box>
                )}
                <NotificationBell />

                <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <Avatar
                            sx={{
                                width: isMobile ? 14 : 16,
                                height: isMobile ? 14 : 16,
                                bgcolor: "#fff",
                                color: "#1677fa",
                                border: "2px solid white",
                                fontSize: 12,
                                cursor: "pointer",
                            }}
                            onClick={handleClick}
                        >
                            <UserOutlined style={{ fontSize: 12 }} />
                        </Avatar>
                    }
                >
                    <Avatar
                        alt={fullName}
                        src={!imageError ? azureImageUrl : undefined}
                        onError={() => setImageError(true)}
                        sx={{
                            width: isMobile ? 36 : 40,
                            height: isMobile ? 36 : 40,
                            bgcolor: "#f0f0f0",
                            fontWeight: "bold",
                            cursor: "pointer",
                            fontSize: 14,
                        }}
                        onClick={handleClick}
                    >
                        {fullName?.split(" ").map((n) => n[0]).join("").toUpperCase() || "U"}
                    </Avatar>
                </Badge>

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-end"
                    transition
                    modifiers={[
                        { name: "offset", options: { offset: [0, 8] } },
                        { name: "preventOverflow", options: { padding: 8 } },
                    ]}
                    style={{ zIndex: 3000 }}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
                            <Paper
                                sx={{
                                    borderRadius: 3,
                                    p: 2,
                                    width: isMobile ? 240 : 260,
                                    maxWidth: "calc(100vw - 24px)",
                                    backgroundColor: "#fafafa",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <Avatar
                                            src={!imageError ? azureImageUrl : undefined}
                                            onError={() => setImageError(true)}
                                            sx={{ width: 64, height: 64, bgcolor: "#e0e0e0", fontSize: 20, mb: 1.5 }}
                                        >
                                            {fullName?.split(" ").map((n) => n[0]).join("").toUpperCase() || "U"}
                                        </Avatar>

                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {fullName}
                                        </Typography>

                                        <Divider sx={{ width: "100%", my: 2 }} />

                                        <MuiButton
                                            fullWidth
                                            size="medium"
                                            startIcon={<UserOutlined />}
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#1677fa",
                                                color: "#fff",
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                mb: 1,
                                                ":hover": { backgroundColor: "#145fd1" },
                                            }}
                                            onClick={() => {
                                                handleClose();
                                                navigate("/profile");
                                            }}
                                        >
                                            Go to Profile
                                        </MuiButton>

                                        <MuiButton
                                            fullWidth
                                            size="medium"
                                            startIcon={<LogoutOutlinedIcon />}
                                            variant="outlined"
                                            sx={{
                                                borderColor: "#ff4d4f",
                                                color: "#ff4d4f",
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                ":hover": {
                                                    backgroundColor: "rgba(255,77,79,0.05)",
                                                    borderColor: "#ff4d4f",
                                                },
                                            }}
                                            onClick={handleLogout}
                                        >
                                            Logout
                                        </MuiButton>
                                    </Box>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Box>
        </header>
    );
};

export default HeaderBar;
